import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Disclosure,
  Input,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, RotateCcw, X } from "lucide-react";
import { useState } from "react";

import { displayError } from "../api/errors.js";
import { ErrorState, LoadingState } from "../components/states.js";
import {
  ANSWERS_SHOWN,
  cleanAnswers,
  listRuleAnswers,
  moveAnswer,
  readingLabel,
  resetRuleAnswers,
  saveRuleAnswers,
  splitReadings,
  type RuleAnswers,
} from "./rule-answers-api.js";

const queryKey = ["rule-answers"];

function ReadingCard({ rule }: { rule: RuleAnswers }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string[] | null>(null);
  const answers = draft ?? rule.answers;
  const label = readingLabel(rule);
  const done = async (saved: RuleAnswers) => {
    queryClient.setQueryData<RuleAnswers[]>(queryKey, (current) =>
      current?.map((entry) =>
        entry.rule_code === saved.rule_code ? saved : entry,
      ),
    );
    setDraft(null);
  };
  const save = useMutation({
    mutationFn: () => saveRuleAnswers(rule.rule_code, cleanAnswers(answers)),
    onSuccess: done,
  });
  const reset = useMutation({
    mutationFn: () => resetRuleAnswers(rule.rule_code),
    onSuccess: done,
  });
  const busy = save.isPending || reset.isPending;
  const error = save.error ?? reset.error;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base">{label}</CardTitle>
        {rule.custom && draft === null ? (
          <Button
            disabled={busy}
            onClick={() => reset.mutate()}
            size="sm"
            variant="ghost"
          >
            <RotateCcw aria-hidden="true" className="size-3.5" /> Reset
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-2 pt-3">
        {answers.map((answer, index) => (
          <div className="flex items-center gap-1" key={index}>
            <Input
              aria-label={`${label}: answer ${index + 1}`}
              className="flex-1"
              maxLength={80}
              onChange={(event) =>
                setDraft(
                  answers.map((entry, position) =>
                    position === index ? event.target.value : entry,
                  ),
                )
              }
              value={answer}
            />
            <Button
              aria-label={`Move “${answer}” up`}
              disabled={busy || index === 0}
              onClick={() => setDraft(moveAnswer(answers, index, index - 1))}
              size="sm"
              variant="ghost"
            >
              <ArrowUp aria-hidden="true" className="size-4" />
            </Button>
            <Button
              aria-label={`Move “${answer}” down`}
              disabled={busy || index === answers.length - 1}
              onClick={() => setDraft(moveAnswer(answers, index, index + 1))}
              size="sm"
              variant="ghost"
            >
              <ArrowDown aria-hidden="true" className="size-4" />
            </Button>
            <Button
              aria-label={`Remove “${answer}”`}
              disabled={busy}
              onClick={() =>
                setDraft(answers.filter((_, position) => position !== index))
              }
              size="sm"
              variant="ghost"
            >
              <X aria-hidden="true" className="size-4" />
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          {answers.length < ANSWERS_SHOWN ? (
            <Button
              disabled={busy}
              onClick={() => setDraft([...answers, ""])}
              size="sm"
              variant="outline"
            >
              <Plus aria-hidden="true" className="size-3.5" /> Add answer
            </Button>
          ) : (
            <span />
          )}
          {draft !== null ? (
            <div className="flex gap-2">
              <Button
                disabled={busy}
                onClick={() => {
                  save.reset();
                  setDraft(null);
                }}
                size="sm"
                variant="ghost"
              >
                Cancel
              </Button>
              <Button disabled={busy} onClick={() => save.mutate()} size="sm">
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          ) : null}
        </div>
        {error ? (
          <p className="text-sm font-semibold text-red-800" role="alert">
            {displayError(error)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

// The one-tap answers staff see when a reading is outside the usual range,
// set for the whole business.
export function RuleAnswersPanel() {
  const rules = useQuery({ queryFn: listRuleAnswers, queryKey });

  if (rules.isPending) return <LoadingState label="Loading answers" />;
  if (rules.error)
    return (
      <ErrorState
        message={displayError(rules.error)}
        onAction={() => void rules.refetch()}
        title="Couldn’t load answers"
      />
    );

  const { everyday, specialist } = splitReadings(rules.data);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-2">
        {everyday.map((rule) => (
          <ReadingCard key={rule.rule_code} rule={rule} />
        ))}
      </div>
      {specialist.length > 0 ? (
        <Disclosure summary="Specialist processes">
          <div className="grid gap-4 pt-2 xl:grid-cols-2">
            {specialist.map((rule) => (
              <ReadingCard key={rule.rule_code} rule={rule} />
            ))}
          </div>
        </Disclosure>
      ) : null}
    </div>
  );
}
